import { useMemo, useState } from "react";
import useAccountPageParams from "../../../../hooks/useAccountPageParams";
import { MagnifyingGlassIcon, CalendarIcon, PlusIcon } from "@heroicons/react/20/solid";
import {
  Alert,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import type { AssociatedObject } from "../../../../shared/emails-meetings/types";
import useObjectSearchTable from "../../../../shared/table/useObjectSearchTable";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import MeetingListItem from "../../../../shared/emails-meetings/MeetingListItem";
import MeetingCard from "../../../../shared/emails-meetings/MeetingCard";
import MeetingCreationModal from "../../../../shared/emails-meetings/MeetingCreationModal";
import MemberLevelIntegrationProvider from "../../../../shared/integration/MemberLevelIntegrationProvider";
import { getMeetingViewModel, meetingMatchesQuery, safeDate } from "../../../../shared/emails-meetings/meetingUtils";
import ObjectDetailSheet from "../account-custom-object/renderers/ObjectDetailSheet";
import EmptyTabState from "../../../../shared/ui/EmptyTabState";
import ViewModeToggle from "../../../../shared/ui/ViewModeToggle";
import { parseObjectSearchRecord } from "../../../../shared/emails-meetings/objectSearchAdapter";

type FilterValue = "all" | "upcoming" | "past";

const AccountMeetings = () => {
  const { accountId }: any = useAccountPageParams();
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<FilterValue>("all");
  const [viewMode, setViewMode] = useState<"list" | "card">("list");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<AssociatedObject | null>(null);

  const [creationOpen, setCreationOpen] = useState(false);

  // Scope filter: only meetings associated with this account
  const scopedFilter = useMemo(() => ({
    query_type: "AND",
    predicates: [{
      field: "accounts.external_id",
      operator: "eq",
      type: "string",
      value: accountId,
      entity_type: "column",
    }],
  }), [accountId]);

  const {
    rows,
    total,
    pageCount,
    pageIndex,
    setPageIndex,
    isLoading,
    isFetching,
    error,
  } = useObjectSearchTable<AssociatedObject>({
    workspaceId: workspaceId || "",
    entityType: "meetings",
    enabled: !!workspaceId && !!accountId,
    initialPageSize: 50,
    initialSort: {
      field: "meetings.created_at",
      order: "desc",
    },
    queryOptions: {
      filter: scopedFilter,
      fallbackSortField: "meetings.created_at",
    },
    parseRows: (rawRows: any[]) =>
      (rawRows || []).map((record: any) => parseObjectSearchRecord(record, "meetings")),
    resetPageDeps: [accountId],
  });

  // Client-side filtering: search + upcoming/past
  const filteredMeetings = useMemo(() => {
    const now = new Date();

    const searched = (rows as AssociatedObject[]).filter(
      (meeting: AssociatedObject) => meetingMatchesQuery(meeting, searchQuery),
    );

    const enriched = searched.map((meeting: AssociatedObject) => {
      const view = getMeetingViewModel(meeting);
      const start = safeDate(view.startTime);
      return { meeting, start };
    });

    type Enriched = { meeting: AssociatedObject; start: Date | null };

    const filtered = enriched.filter(({ start }: Enriched) => {
      if (filter === "all") return true;
      if (!start) return false;
      return filter === "upcoming" ? start > now : start <= now;
    });

    // Sort: upcoming ascending, past descending
    const upcoming = filtered
      .filter(({ start }: Enriched) => !!start && start > now)
      .sort((a: Enriched, b: Enriched) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0))
      .map(({ meeting }: Enriched) => meeting);

    const past = filtered
      .filter(({ start }: Enriched) => !start || start <= now)
      .sort((a: Enriched, b: Enriched) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0))
      .map(({ meeting }: Enriched) => meeting);

    if (filter === "upcoming") return upcoming;
    if (filter === "past") return past;
    return [...upcoming, ...past];
  }, [rows, filter, searchQuery]);

  const handleOpenMeeting = (meeting: AssociatedObject) => {
    setSelectedMeeting(meeting);
    setDetailsOpen(true);
  };

  const handlePageChange = (page: number) => {
    setPageIndex(page - 1); // Mantine Pagination is 1-indexed
  };

  const emptyMessage = useMemo(() => {
    if (searchQuery || filter !== "all") return "Try adjusting your search or filters.";
    return "Meetings associated with this account will appear here.";
  }, [filter, searchQuery]);

  const totalPages = pageCount;

  return (
    <MemberLevelIntegrationProvider>
      <div className="h-full bg-white flex flex-col overflow-hidden">
        <LoadingOverlay
          loaderProps={{ size: "sm" }}
          c="main.5"
          visible={isLoading}
          overlayProps={{ blur: 2 }}
        />

      <Stack gap={10} className="px-4 pt-4 pb-3">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={700} c="text.9" size="lg">Meetings</Text>
            <Text size="xs" c="text.5">
              {isLoading ? "..." : String(total)} meetings
            </Text>
          </Stack>

          <Group gap="sm">
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <Button
              size="sm"
              variant="filled"
              color="main"
              leftSection={<PlusIcon className="h-4 w-4" />}
              onClick={() => setCreationOpen(true)}
            >
              Create Meeting
            </Button>
          </Group>
        </Group>

        <Group gap="sm" align="center" wrap="wrap">
          <SegmentedControl
            value={filter}
            onChange={(value) => setFilter(value as FilterValue)}
            data={[
              { label: "All", value: "all" },
              { label: "Upcoming", value: "upcoming" },
              { label: "Past", value: "past" },
            ]}
            radius="md"
            size="sm"
            color="main"
          />

          <TextInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            placeholder="Search title, organizer, attendees..."
            leftSection={<MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />}
            size="sm"
            radius="md"
            style={{ flex: 1, minWidth: 220 }}
          />
        </Group>
      </Stack>

      <Divider />

      <ScrollArea className="flex-1" type="scroll" scrollHideDelay={500}>
        <Stack gap={0}>
          {!!error && (
            <div className="p-4">
              <Alert color="red" variant="light" radius="md">
                Failed to load meetings. Please try again.
              </Alert>
            </div>
          )}

          {!isLoading && !error && filteredMeetings.length === 0 && (
            <EmptyTabState
              title="No meetings yet"
              description={emptyMessage}
              icon={<CalendarIcon className="h-6 w-6" />}
            />
          )}

          {viewMode === "card" ? (
            <div className="grid gap-3 p-4">
              {filteredMeetings.map((meeting: any) => (
                <MeetingCard key={meeting.id} meeting={meeting} />
              ))}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredMeetings.map((meeting: any) => (
                <MeetingListItem key={meeting.id} meeting={meeting} onOpen={handleOpenMeeting} />
              ))}
            </div>
          )}
        </Stack>
      </ScrollArea>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="p-3 border-t border-gray-100 bg-gray-50">
          <Group justify="center">
            <Pagination
              total={totalPages}
              value={pageIndex + 1}
              onChange={handlePageChange}
              size="sm"
              color="main"
              siblings={1}
            />
          </Group>
        </div>
      )}

      {selectedMeeting && (() => {
        const view = getMeetingViewModel(selectedMeeting);
        const start = safeDate(view.startTime);
        const isUpcoming = start ? start > new Date() : false;
        return (
          <ObjectDetailSheet
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            title={view.title}
            badge={isUpcoming ? { label: "Upcoming" } : { label: "Past" }}
            sourceUrl={view.meetingUrl}
            sourceLabel="Join Meeting"
            fields={[
              { label: "Organizer", value: view.organizer },
              { label: "Duration", value: (() => {
                if (!view.durationSeconds) return "";
                const minutes = Math.round(view.durationSeconds / 60);
                const hours = Math.floor(minutes / 60);
                const rem = minutes % 60;
                const parts: string[] = [];
                if (hours) parts.push(`${hours}h`);
                if (rem) parts.push(`${rem}m`);
                return parts.join(" ");
              })() },
              { label: "Start", value: view.startTime, type: "date" },
              { label: "End", value: view.endTime, type: "date" },
              { label: "Source", value: view.source },
              { label: "Created", value: selectedMeeting.created_at, type: "date" },
              { label: "Participants", value: view.participants, type: "participants" },
            ]}
          />
        );
      })()}

        <MeetingCreationModal
          opened={creationOpen}
          onClose={() => setCreationOpen(false)}
        />
      </div>
    </MemberLevelIntegrationProvider>
  );
};

export default AccountMeetings;
